/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useEffect, useState } from 'react';
import { PREMIUM_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import type { TenantContext } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import { SERVICE_DESK, ASSETS } from '@jira/platform__application-editions/src/application-key.tsx';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { performCmdbGetRequest } from '@jira/servicedesk__insight-common-utils-cmdb-client/src/performCmdbGetRequest.tsx';
import { isCMDBEnabledInEnvironment } from '@jira/servicedesk__insight-common-utils-cmdb-fedramp/src/isCMDBEnabledInEnvironment.tsx';
import type { WorkspaceId } from '@jira/servicedesk__insight-common-types-shared-types/src/toCmdbObjectTypeId.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { ResourceStoreContext, RouterDataContext } from '@atlassian/react-resource-router';
import type { AssetsTenantContext } from '@atlassian/jira-assets-tenant-context-provider/src/AssetsTenantContext.types.tsx';
import type { UseCheckAssetsUser } from './WorkspaceContext.types.tsx';
import {
	validateTenantContext,
	createDefaultResponse,
	createSuccessResponse,
	fetchUserPermissions,
} from './createSuccessResponse.tsx';

export const getWorkspaceUrl = () => '/rest/servicedesk/cmdb/latest/workspace';

type ResultType = {
	id: string;
};

export type WorkspaceQueryResponse = {
	size: number;
	hasNextPage: boolean;
	results: ResultType[];
};

type UserJsmLicenceState =
	| { siteHasJsmInstalled: false }
	| { siteHasJsmInstalled: true; siteIsPremium: boolean; userLicensed: boolean };

export const getUserJsmLicenceState = (tenantContext: AssetsTenantContext): UserJsmLicenceState => {
	const { licensedProducts, appPermissions } = tenantContext;
	const hasJsmLicence = licensedProducts && licensedProducts[SERVICE_DESK];
	const isJsmLicensedUser = appPermissions && appPermissions.hasServiceDeskAccess;

	if (hasAssetsEntitlement(tenantContext) && fg('aurum-997-license-kill-switch')) {
		return {
			userLicensed: tenantContext?.isLicensedAssetsUser === true,
			siteHasJsmInstalled: true,
			siteIsPremium: true,
		};
	}

	if (!hasJsmLicence) {
		return { siteHasJsmInstalled: false };
	}

	return {
		siteHasJsmInstalled: true,
		siteIsPremium: isPremiumInstance(tenantContext),
		userLicensed: isJsmLicensedUser,
	};
};

export const hasAssetsEntitlement = (tenantContext: TenantContext): boolean => {
	const { licensedProducts } = tenantContext;
	const containsAssetsEntitlement = licensedProducts && licensedProducts[ASSETS];
	return containsAssetsEntitlement;
};

export const isPremiumInstance = (tenantContext: TenantContext): boolean => {
	const {
		appEditions: { serviceDesk: serviceDeskAppEdition },
	} = tenantContext;

	const isPremiumInstanceEdition = serviceDeskAppEdition === PREMIUM_EDITION;

	return isPremiumInstanceEdition;
};

export const isPremiumAssetsInstance = (tenantContext: TenantContext): boolean => {
	const {
		appEditions: { serviceDesk: serviceDeskAppEdition },
	} = tenantContext;

	const isPremiumInstanceEdition = serviceDeskAppEdition === PREMIUM_EDITION;

	if (hasAssetsEntitlement(tenantContext) && fg('aurum-997-license-kill-switch')) {
		return true;
	}

	return isPremiumInstanceEdition;
};

export const isLicensedPremiumUser = (tenantContext: TenantContext): boolean => {
	const { appPermissions } = tenantContext;

	const isPremium = isPremiumInstance(tenantContext);
	const isLicensedUser = appPermissions && appPermissions.hasServiceDeskAccess;

	return isPremium && isLicensedUser;
};

/**
 *
 * @param tenantContext - This tenant context contains the app permissions and the licensed products and response from global config api from SSR to check if user is licensed
 * @description - This function checks if the user is licensed to use the Assets app by checking the following in rollout phases
 * - For the first phase rely on existing JSM checks
 * - Mid rollout phase user might have to rely on entitlement checks for Assets in TCS data and confirm if the user is licensed to use the Assets app + old checks
 * - Post rollout phase user depends solely on assets in TCS data as entitlement check and user access via global/config API - if it succeeds then user is licensed, if it fails then user is not licensed
 * @returns
 */
export const isAssetsUser = (tenantContext: AssetsTenantContext): boolean => {
	const { appPermissions } = tenantContext;

	const isPremium = isPremiumInstance(tenantContext);
	const isLicensedJsmUser = appPermissions && appPermissions.hasServiceDeskAccess;

	if (hasAssetsEntitlement(tenantContext) && fg('aurum-997-license-kill-switch')) {
		return tenantContext?.isLicensedAssetsUser === true;
	}

	return isPremium && isLicensedJsmUser;
};

const transformResponse = (response: WorkspaceQueryResponse): WorkspaceId | null => {
	const ids = response.results ?? [];
	return ids.length ? ids[0].id : null;
};

const ERROR_META = {
	id: 'checkIfAssetsUser',
	packageName: 'jiraServicedeskInsightCommonWorkspaceContext',
	teamName: 'astral',
} as const;

const INITIAL_LOADING_STATE: UseCheckAssetsUser = {
	isLoading: true,
	isAssetsAdmin: false,
	error: null,
	isValidAssetsUser: null,
};

type PermissionSource = 'jsm' | 'assets' | 'combined' | 'units';

const getPermissionSource = (
	tenantContext: TenantContext,
	unitAwareWorkspaceId?: string,
): PermissionSource => {
	// Units path is controlled directly by the workspace-host resolver gate and
	// must not be blocked by legacy JSM kill-switch branching.
	if (unitAwareWorkspaceId && fg('astral_units_workspace_host_resolver')) {
		return 'units';
	}

	if (!fg('aurum-997-license-kill-switch')) {
		return 'jsm';
	}

	if (hasAssetsEntitlement(tenantContext) && fg('jsm_assets_use_assets_resource_for_permissions')) {
		return 'assets';
	}

	if (hasAssetsEntitlement(tenantContext) && fg('aaap_use_assets_and_jsm_roles')) {
		return 'combined';
	}

	return 'jsm';
};

const getJSMPermissions = (tenantContext: TenantContext): UseCheckAssetsUser => ({
	isLoading: false,
	isAssetsAdmin: tenantContext.isAdmin,
	error: null,
	isValidAssetsUser: isLicensedPremiumUser(tenantContext),
});

/**
 * Fetches Assets permissions from API.
 * Returns early with JSM permissions when kill switch is OFF or no API call is needed.
 */
export const checkIfAssetsUser = async (
	tenantContext: TenantContext,
	unitAwareWorkspaceId?: string,
): Promise<UseCheckAssetsUser> => {
	const validation = validateTenantContext(tenantContext);
	if (!validation.isValid) {
		fireErrorAnalytics({ meta: ERROR_META, error: validation.error });
		return createDefaultResponse(validation?.error ?? null);
	}

	const permissionSource = getPermissionSource(tenantContext, unitAwareWorkspaceId);
	if (permissionSource === 'jsm') {
		return createSuccessResponse(false, false);
	}

	try {
		const { isValidAssetsUser, isAssetsAdmin } = await fetchUserPermissions(tenantContext);
		return createSuccessResponse(isValidAssetsUser, isAssetsAdmin);
	} catch (error: unknown) {
		fireErrorAnalytics({
			meta: ERROR_META,
			error: error instanceof Error ? error : new Error(String(error)),
		});
		return createDefaultResponse(new Error('Error while fetching user permissions'));
	}
};

/**
 * Hook to check if the current user has Assets permissions.
 *
 * Permission source depends on feature flags:
 * - Kill switch OFF â†’ JSM permissions
 * - Kill switch ON + Cutover ON + Assets entitlement â†’ Assets permissions (API only)
 * - Kill switch ON + Mid rollout ON â†’ Combined JSM + Assets permissions
 * - Kill switch ON + both flags OFF â†’ JSM permissions
 */
export const useCheckAssetsUser = (
	tenantContext: TenantContext,
	unitAwareWorkspaceId?: string,
): UseCheckAssetsUser => {
	const permissionSource = getPermissionSource(tenantContext, unitAwareWorkspaceId);
	const shouldFetchFromAPI = permissionSource !== 'jsm';

	const [permissions, setPermissions] = useState<UseCheckAssetsUser>(INITIAL_LOADING_STATE);

	useEffect(() => {
		if (!shouldFetchFromAPI) {
			return;
		}

		// Stale closure flag - prevents setState on unmounted component or stale requests
		// See: https://react.dev/learn/synchronizing-with-effects#fetching-data
		let isStale = false;

		checkIfAssetsUser(tenantContext, unitAwareWorkspaceId)
			.then((result) => {
				if (isStale) return;

				if (permissionSource === 'assets' || permissionSource === 'units') {
					setPermissions(result);
				} else {
					const jsmPermissions = getJSMPermissions(tenantContext);
					setPermissions({
						...result,
						isValidAssetsUser: jsmPermissions.isValidAssetsUser || result.isValidAssetsUser,
						isAssetsAdmin: jsmPermissions.isAssetsAdmin || result.isAssetsAdmin,
					});
				}
			})
			.catch((error: unknown) => {
				if (isStale) return;
				setPermissions({
					isLoading: false,
					isAssetsAdmin: false,
					error: error instanceof Error ? error : new Error('Unknown error'),
					isValidAssetsUser: null,
				});
			});

		return () => {
			isStale = true;
		};
	}, [shouldFetchFromAPI, permissionSource, tenantContext, unitAwareWorkspaceId]);

	if (!shouldFetchFromAPI) {
		return getJSMPermissions(tenantContext);
	}

	return permissions;
};

export const fetchWorkspaceId = (
	routerContext: RouterDataContext,
	{ tenantContext }: ResourceStoreContext,
): Promise<string | null> | null => {
	const isNotLicensedAssetsUser = fg('aurum-997-license-kill-switch')
		? hasAssetsEntitlement(tenantContext) === false
		: isLicensedPremiumUser(tenantContext) === false;

	if (isNotLicensedAssetsUser || !isCMDBEnabledInEnvironment()) {
		return null;
	}
	return performCmdbGetRequest(getWorkspaceUrl())
		.then(transformResponse)
		.catch((error) => {
			log.safeErrorWithoutCustomerData(
				'spa.view.routes.servicedesk.workspace-id',
				'Error while fetching servicedesk Insight workspace id',
				error,
			);
			return null;
		});
};

export default fetchWorkspaceId;
